import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Home } from "lucide-react";
import { LightToggle } from "../components/LightToggle";

const GOLD = "var(--altar-gold)";
const GOLD_FADE = "var(--altar-gold-fade)";
const GOLD_SOFT = "var(--altar-gold-soft)";
const CHARCOAL = "var(--altar-charcoal)";
const MUTED = "var(--altar-muted)";

const BOARDS = [
  { glyph: "✦", title: "Begin", caption: "a new prayer", key: "begin" },
  { glyph: "❧", title: "My Library", caption: "saved prayers", key: "library" },
  { glyph: "☾", title: "Sign in", caption: "continue yours", key: "signin" },
];

const STEPS = [
  { n: "1", title: "Share your faith", caption: "and where it comes from" },
  { n: "2", title: "Speak your heart", caption: "the moment you are in" },
  { n: "3", title: "Receive your prayer", caption: "in the voice of your tradition" },
];

const Eyebrow = ({ children, testId }) => (
  <p
    className="text-[13px] tracking-[0.3em] uppercase"
    style={{ color: MUTED }}
    data-testid={testId}
  >
    {children}
  </p>
);

export default function Landing() {
  const { user } = useAuth();
  const [waiting, setWaiting] = useState(null);
  const [today, setToday] = useState(null);
  const beginTarget = "/builder";
  const boardLinks = { begin: beginTarget, library: "/library", signin: "/login" };

  useEffect(() => {
    api.get("/well/status").then(({ data }) => setWaiting(data.waiting)).catch(() => {});
    api.get("/stats/today").then(({ data }) => setToday(data)).catch(() => {});
  }, []);

  return (
    <div
      className="min-h-screen font-altar p-3 sm:p-5"
      style={{ backgroundColor: "var(--altar-page)" }}
      data-testid="altar-landing"
    >
      {/* Outer gold border */}
      <div
        className="min-h-[calc(100vh-2.5rem)] rounded-md p-1.5"
        style={{ border: `1.5px solid ${GOLD}` }}
      >
        {/* Inner hairline border enclosing the cream surface */}
        <div
          className="min-h-full rounded-sm"
          style={{ border: `0.5px solid ${GOLD}`, backgroundColor: "var(--altar-surface)" }}
        >
          {/* Top nav */}
          <nav className="flex items-center justify-between px-6 sm:px-10 py-5" data-testid="altar-nav">
            <div className="flex items-center gap-3 sm:gap-4">
            <Link
              to="/"
              data-testid="nav-home-icon"
              aria-label="Home"
              title="Home"
              className="inline-flex items-center justify-center cursor-pointer transition-opacity hover:opacity-70"
              style={{ color: GOLD }}
            >
              <Home className="w-5 h-5" strokeWidth={1.25} />
            </Link>
            <Link to="/" className="flex items-center gap-2 cursor-pointer" data-testid="nav-brand">
              <span
                className="w-2 h-2 rounded-full inline-block"
                style={{ backgroundColor: GOLD }}
                aria-hidden="true"
              />
              <span className="text-2xl" style={{ color: CHARCOAL, fontWeight: 500 }}>
                Inlight
              </span>
            </Link>
            </div>
            <div className="flex items-center gap-5 sm:gap-7 text-base" style={{ color: MUTED }}>
              <LightToggle color={GOLD} />
              <a href="#how-it-unfolds" className="hover:opacity-70 transition-opacity" data-testid="nav-about">
                About
              </a>
              <Link to="/well" className="hover:opacity-70 transition-opacity" data-testid="nav-well">
                The Well
              </Link>
              <Link to="/login" className="hover:opacity-70 transition-opacity" data-testid="nav-signin">
                Sign in
              </Link>
              <Link
                to={beginTarget}
                className="px-5 py-1.5 rounded-full text-sm transition-colors hover:bg-white"
                style={{ border: `1px solid ${GOLD}`, color: CHARCOAL }}
                data-testid="nav-begin"
              >
                Begin
              </Link>
            </div>
          </nav>
          <div className="mx-6 sm:mx-10" style={{ height: "1px", backgroundColor: GOLD_FADE }} aria-hidden="true" />

          {/* Hero */}
          <header className="altar-fade text-center px-6 pt-16 sm:pt-20 pb-14">
            <Eyebrow testId="hero-eyebrow">— A quiet place for prayer —</Eyebrow>
            <h1
              className="font-square uppercase mx-auto mt-8 text-[24px] sm:text-[32px] leading-[1.45] tracking-[0.04em]"
              style={{ color: CHARCOAL, fontWeight: 500, maxWidth: "720px" }}
              data-testid="hero-title"
            >
              Prayers shaped around your faith, your heart, and the moment you are in.
            </h1>
            <p
              className="mt-7 italic text-[17px]"
              style={{ color: MUTED }}
              data-testid="hero-promise"
            >
              Words your heart can receive and return to, every day.
            </p>
          </header>

          {/* Three boards */}
          <section className="px-6 sm:px-10 pb-16" data-testid="altar-boards">
            <div className="max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5">
              {BOARDS.map((b) => (
                <Link
                  key={b.key}
                  to={boardLinks[b.key]}
                  className="block text-center bg-white rounded-sm py-8 px-4 transition-shadow hover:shadow-md"
                  style={{
                    border: `0.5px solid ${GOLD}`,
                    boxShadow: "0 2px 10px rgba(184,147,90,0.08)",
                  }}
                  data-testid={`board-${b.key}`}
                >
                  <span className="block text-2xl mb-3" style={{ color: GOLD_SOFT }} aria-hidden="true">
                    {b.glyph}
                  </span>
                  <span className="block text-xl" style={{ color: CHARCOAL, fontWeight: 500 }}>
                    {b.title}
                  </span>
                  <span className="block mt-1 text-sm" style={{ color: MUTED }}>
                    {b.caption}
                  </span>
                </Link>
              ))}
            </div>
          </section>

          {/* Ornament divider */}
          <div className="text-center pb-14" aria-hidden="true">
            <span className="text-sm" style={{ color: GOLD }}>✦</span>
          </div>

          {/* A glimpse of Inlight */}
          <section className="px-6 pb-16 text-center" data-testid="sample-prayer-section">
            <Eyebrow testId="sample-prayer-label">A glimpse of Inlight</Eyebrow>
            <div
              className="max-w-xl mx-auto mt-8 bg-white rounded-sm px-8 py-9"
              style={{
                border: `0.5px solid ${GOLD}`,
                boxShadow: "0 2px 10px rgba(184,147,90,0.08)",
              }}
            >
              <p
                className="text-[13px] tracking-[0.25em] uppercase mb-6"
                style={{ color: MUTED }}
                data-testid="sample-prayer-title"
              >
                A moment of peace
              </p>
              <p
                className="italic text-[18px]"
                style={{ color: CHARCOAL, lineHeight: 1.75 }}
                data-testid="sample-prayer-body"
              >
                I am held in this quiet morning, and gratitude rises gently in my heart.
                Peace flows through me now, calm and steady, like first light over still
                water. I carry strength into this day, and I trust the good that is
                already on its way.
              </p>
              <p className="mt-6 text-base" style={{ color: GOLD }} aria-hidden="true">
                ❧
              </p>
            </div>
          </section>

          {/* How it unfolds */}
          <section id="how-it-unfolds" className="px-6 pb-20 text-center" data-testid="how-it-unfolds">
            <Eyebrow testId="how-label">How it unfolds</Eyebrow>
            <div className="max-w-2xl mx-auto mt-10 grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-6">
              {STEPS.map((s) => (
                <div key={s.n} data-testid={`step-${s.n}`}>
                  <span
                    className="mx-auto flex items-center justify-center w-9 h-9 rounded-full text-base"
                    style={{ border: `1px solid ${GOLD}`, color: CHARCOAL }}
                  >
                    {s.n}
                  </span>
                  <p className="mt-4 text-lg" style={{ color: CHARCOAL, fontWeight: 500 }}>
                    {s.title}
                  </p>
                  <p className="mt-1 text-sm" style={{ color: MUTED }}>
                    {s.caption}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* The Prayer Well — the pathway to gifting */}
          <section className="px-6 sm:px-10 pb-20" data-testid="altar-well">
            <div
              className="max-w-3xl mx-auto text-center bg-white rounded-sm px-8 sm:px-12 py-12"
              style={{ border: `0.5px solid ${GOLD}`, boxShadow: "0 2px 10px rgba(184,147,90,0.08)" }}
              data-testid="well-invite"
            >
              <span className="block text-2xl mb-5" style={{ color: GOLD_SOFT }} aria-hidden="true">✦</span>
              <Eyebrow testId="well-invite-label">The Prayer Well</Eyebrow>
              <p
                className="mt-6 font-altar italic text-xl sm:text-2xl leading-relaxed max-w-xl mx-auto"
                style={{ color: CHARCOAL }}
                data-testid="well-invite-hook"
              >
                Somewhere tonight, someone cannot find the words.
              </p>
              <p
                className="mt-5 text-base leading-relaxed max-w-xl mx-auto"
                style={{ color: MUTED }}
                data-testid="well-invite-body"
              >
                A gift of five dollars writes ten prayers — each one shaped in a stranger's own
                tradition and left here for them to find. You never learn who receives yours.
                They never learn who sent it. Only that someone thought of them.
              </p>

              <div className="mt-9 flex flex-col items-center gap-4">
                <Link
                  to="/well#well-support"
                  data-testid="well-gift-cta"
                  className="inline-block px-8 py-3 rounded-full text-base transition-colors"
                  style={{ backgroundColor: GOLD, color: "var(--altar-charcoal)" }}
                >
                  Gift prayers for a stranger
                </Link>
                <Link
                  to="/well"
                  data-testid="well-receive-cta"
                  className="text-base underline decoration-1 underline-offset-4 transition-opacity hover:opacity-70"
                  style={{ color: GOLD_SOFT }}
                >
                  or quietly receive one →
                </Link>
              </div>

              <p className="mt-8 text-sm italic" style={{ color: MUTED }} data-testid="landing-well-counter">
                {waiting == null
                  ? "\u00A0"
                  : waiting > 0
                    ? `${waiting} ${waiting === 1 ? "prayer waits" : "prayers wait"} in the well right now.`
                    : "The well is quiet tonight — a gift would fill it."}
              </p>
            </div>
          </section>

          {/* Footer */}
          <footer className="px-6 sm:px-10 pb-10 text-center" data-testid="altar-footer">
            <div className="mb-8" style={{ height: "1px", backgroundColor: GOLD_FADE }} aria-hidden="true" />
            <p className="italic text-sm mb-6" style={{ color: MUTED }} data-testid="today-across-inlight">
              {!today
                ? "\u00A0"
                : today.prayers_written + today.gifted + today.received === 0
                  ? "The first prayer of the day is waiting to be written."
                  : `Today across Inlight — ${today.prayers_written} ${today.prayers_written === 1 ? "prayer" : "prayers"} written, ${today.gifted} gifted, ${today.received} received.`}
            </p>
            <p className="text-[13px] tracking-[0.3em] uppercase" style={{ color: MUTED }}>
              — Inlight —
            </p>
            <p className="mt-2 italic text-base" style={{ color: MUTED }}>
              A quiet place for prayer.
            </p>
          </footer>
        </div>
      </div>
    </div>
  );
}
